import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Upload } from 'lucide-react';

import {
  operationsApi,
  type AdvanceDto,
  type AttendanceRow,
  type DocumentDto,
  type DutySession,
  type EngagementDetail,
  type WorkUnit,
} from '@/features/operations/api';
import {
  financialApi,
  type Fine,
  type Payslip,
  type SalaryStructure,
} from '@/features/financial/api';
import { commandCenterApi, type AlertDto } from '@/features/command-center/api';
import { DocumentUploadModal } from '@/features/operations/components/DocumentUploadModal';
import { SmartLoading } from '@/lib/ui';
import { useAuthStore } from '@/stores/auth-store';

export type FieldUserRow = {
  id: string;
  employee_id: string;
  full_name: string;
  phone: string | null;
  photo_path: string | null;
  status: string;
  hired_at: string | null;
  last_login_at: string | null;
};

const TABS = [
  'Profile',
  'Documents',
  'Financials',
  'Duty History',
  'Work Unit History',
  'Attendance',
  'Salary Structure',
  'Recent Alerts',
] as const;

type Tab = (typeof TABS)[number];

// ── Formatting helpers ─────────────────────────────────────────────────────
function money(minor: number, currency = 'AED') {
  return `${currency} ${(minor / 100).toFixed(2)}`;
}
const fmtDate = (s: string | null | undefined) => (s ? new Date(s).toLocaleDateString() : '—');
const fmtDateTime = (s: string | null | undefined) => (s ? new Date(s).toLocaleString() : '—');

/** ISO range for "the last `days` days", used by the range-scoped endpoints. */
function lastDays(days: number): { start: string; end: string } {
  const end = new Date();
  const start = new Date(end.getTime() - days * 86400_000);
  return { start: start.toISOString(), end: end.toISOString() };
}

export function FieldUserDetailPanel({
  user,
  onClose,
}: {
  user: FieldUserRow;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>('Profile');

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Close detail panel"
        className="flex-1 bg-black/40"
        onClick={onClose}
      />
      <aside className="flex h-full w-full max-w-xl flex-col border-l border-border bg-background shadow-xl">
        <header className="flex items-start justify-between border-b border-border p-4">
          <div>
            <h2 className="text-lg font-semibold">{user.full_name}</h2>
            <p className="font-mono text-xs text-muted-foreground">
              {user.employee_id} · {user.status}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <nav className="flex flex-wrap gap-1 border-b border-border px-2 py-2">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded px-3 py-1.5 text-xs font-medium ${
                tab === t ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {t}
            </button>
          ))}
        </nav>

        <div className="flex-1 overflow-auto p-4">
          {tab === 'Profile' && <ProfileTab user={user} />}
          {tab === 'Documents' && <DocumentsTab user={user} />}
          {tab === 'Financials' && <FinancialsTab user={user} />}
          {tab === 'Duty History' && <DutyHistoryTab user={user} />}
          {tab === 'Work Unit History' && <WorkUnitsTab user={user} />}
          {tab === 'Attendance' && <AttendanceTab user={user} />}
          {tab === 'Salary Structure' && <SalaryTab user={user} />}
          {tab === 'Recent Alerts' && <AlertsTab user={user} />}
        </div>
      </aside>
    </div>
  );
}

// ── Profile ─────────────────────────────────────────────────────────────────
function ProfileTab({ user }: { user: FieldUserRow }) {
  return (
    <dl className="grid grid-cols-2 gap-4 text-sm">
      <Field label="Employee ID" value={user.employee_id} mono />
      <Field label="Full name" value={user.full_name} />
      <Field label="Status" value={user.status} />
      <Field label="Phone" value={user.phone ?? '—'} />
      <Field label="Hired" value={fmtDate(user.hired_at)} />
      <Field
        label="Last login"
        value={user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'Never'}
      />
    </dl>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={`mt-0.5 ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}

// ── Shared async-state shells ───────────────────────────────────────────────
function TabState({
  isLoading,
  error,
  isEmpty,
  emptyLabel,
  children,
}: {
  isLoading: boolean;
  error: unknown;
  isEmpty: boolean;
  emptyLabel: string;
  children: React.ReactNode;
}) {
  if (isLoading) {
    return <SmartLoading className="py-8 text-center text-sm text-muted-foreground" />;
  }
  if (error) {
    return (
      <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
        {(error as Error).message || 'Failed to load'}
      </p>
    );
  }
  if (isEmpty) {
    return (
      <div className="grid h-32 place-items-center text-center">
        <p className="text-sm text-muted-foreground">{emptyLabel}</p>
      </div>
    );
  }
  return <>{children}</>;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground first:mt-0">
      {children}
    </h3>
  );
}

/**
 * Renders a capture photo from a stored object path. The list endpoints return
 * the raw object path; we exchange it for a short-lived signed URL on demand.
 * Shows a labelled placeholder when there's no photo or storage is unconfigured
 * (signed URL comes back empty), and links to the full image in a new tab.
 */
function MediaThumb({ path, label }: { path: string | null; label: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['media-sign', path],
    queryFn: () => operationsApi.signMedia(path as string),
    enabled: !!path,
    staleTime: 5 * 60_000,
  });

  const base =
    'flex h-16 w-16 shrink-0 items-center justify-center rounded border border-border bg-muted text-center text-[10px] text-muted-foreground';

  if (!path)
    return (
      <div className={base}>
        {label}
        <br />—
      </div>
    );
  if (isLoading)
    return (
      <div className={base}>
        {label}
        <br />…
      </div>
    );
  if (!data?.url)
    return (
      <div className={base}>
        {label}
        <br />
        n/a
      </div>
    );
  return (
    <a
      href={data.url}
      target="_blank"
      rel="noopener noreferrer"
      title={`${label} photo`}
      className="block"
    >
      <img
        src={data.url}
        alt={`${label} photo`}
        className="h-16 w-16 rounded border border-border object-cover"
      />
    </a>
  );
}

const DOC_COLOR: Record<DocumentDto['status_color'], string> = {
  ok: 'bg-emerald-500/15 text-emerald-300',
  warning: 'bg-amber-500/15 text-amber-300',
  expiring: 'bg-orange-500/15 text-orange-300',
  expired: 'bg-rose-500/15 text-rose-300',
};

// ── Documents ───────────────────────────────────────────────────────────────
function DocumentsTab({ user }: { user: FieldUserRow }) {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const canUpload = useMemo(() => {
    const perms = useAuthStore.getState().tenant?.permissions ?? [];
    return perms.includes('document.create');
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['fu-documents', user.id],
    queryFn: () => operationsApi.listFieldUserDocuments(user.id),
  });

  const openDoc = async (id: string) => {
    // The list omits signed URLs; mint one on demand, then open it.
    const doc = await operationsApi.getDocument(id);
    if (doc.download_url) window.open(doc.download_url, '_blank', 'noopener');
  };

  return (
    <div>
      {canUpload && (
        <div className="mb-3 flex justify-end">
          <button className="btn-secondary btn-sm" onClick={() => setUploading(true)}>
            <Upload className="h-4 w-4" />
            Upload document
          </button>
        </div>
      )}
      <TabState
        isLoading={isLoading}
        error={error}
        isEmpty={(data ?? []).length === 0}
        emptyLabel="No documents on file for this field user."
      >
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-foreground">
            <tr className="border-b border-border">
              <th className="py-2 pr-3">Title</th>
              <th className="py-2 pr-3">Type</th>
              <th className="py-2 pr-3">Expires</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(data ?? []).map((d) => (
              <tr key={d.id} className="border-b border-border/60">
                <td className="py-2 pr-3">{d.title}</td>
                <td className="py-2 pr-3 text-muted-foreground">{d.document_type_slug ?? '—'}</td>
                <td className="py-2 pr-3">
                  <span className={`rounded px-2 py-0.5 text-xs ${DOC_COLOR[d.status_color]}`}>
                    {d.expires_at ? fmtDate(d.expires_at) : 'no expiry'}
                  </span>
                </td>
                <td className="py-2 text-right">
                  <button
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    onClick={() => openDoc(d.id)}
                  >
                    <Download className="h-3.5 w-3.5" />
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TabState>

      {uploading && (
        <DocumentUploadModal
          scope="FIELD_USER"
          subjectId={user.id}
          onClose={() => setUploading(false)}
          onUploaded={() => {
            setUploading(false);
            queryClient.invalidateQueries({ queryKey: ['fu-documents', user.id] });
          }}
        />
      )}
    </div>
  );
}

// ── Financials (advances + fines + payslips) ────────────────────────────────
const ADVANCE_COLOR: Record<AdvanceDto['status'], string> = {
  REQUESTED: 'text-amber-300',
  APPROVED: 'text-emerald-300',
  DISBURSED: 'text-cyan-300',
  REJECTED: 'text-rose-300',
  DEDUCTED: 'text-slate-300',
};

function FinancialsTab({ user }: { user: FieldUserRow }) {
  const advances = useQuery({
    queryKey: ['fu-advances', user.id],
    queryFn: () => operationsApi.listAdvancesForUser(user.id),
  });
  const fines = useQuery({
    queryKey: ['fu-fines', user.id],
    queryFn: () => financialApi.listFines(user.id),
  });
  const payslips = useQuery({
    queryKey: ['fu-payslips', user.id],
    queryFn: () => financialApi.listPayslipsForUser(user.id, 12),
  });

  const isLoading = advances.isLoading || fines.isLoading || payslips.isLoading;
  const error = advances.error || fines.error || payslips.error;
  const isEmpty =
    (advances.data ?? []).length === 0 &&
    (fines.data ?? []).length === 0 &&
    (payslips.data ?? []).length === 0;

  return (
    <TabState
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyLabel="No advances, fines or payslips for this field user."
    >
      {(advances.data ?? []).length > 0 && (
        <>
          <SectionTitle>Advances</SectionTitle>
          <table className="w-full text-sm">
            <tbody>
              {(advances.data as AdvanceDto[]).map((a) => (
                <tr key={a.id} className="border-b border-border/60">
                  <td className="py-1.5 font-mono tabular-nums">
                    {money(a.amount_minor, a.currency)}
                  </td>
                  <td className={`py-1.5 ${ADVANCE_COLOR[a.status]}`}>{a.status}</td>
                  <td className="py-1.5 text-xs text-muted-foreground">
                    {fmtDate(a.requested_at)}
                  </td>
                  <td className="py-1.5 text-muted-foreground">{a.reason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {(fines.data ?? []).length > 0 && (
        <>
          <SectionTitle>Fines</SectionTitle>
          <table className="w-full text-sm">
            <tbody>
              {(fines.data as Fine[]).map((f) => (
                <tr key={f.id} className="border-b border-border/60">
                  <td className="py-1.5 font-mono tabular-nums">
                    {money(f.amount_minor, f.currency)}
                  </td>
                  <td className="py-1.5">{f.status}</td>
                  <td className="py-1.5 text-xs text-muted-foreground">{fmtDate(f.issued_on)}</td>
                  <td className="py-1.5 text-muted-foreground">{f.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {(payslips.data ?? []).length > 0 && (
        <>
          <SectionTitle>Payslips</SectionTitle>
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-1.5 pr-3">Period</th>
                <th className="py-1.5 pr-3">Gross</th>
                <th className="py-1.5">Net</th>
              </tr>
            </thead>
            <tbody>
              {(payslips.data as Payslip[]).map((p) => (
                <tr key={p.id} className="border-b border-border/60">
                  <td className="py-1.5 pr-3">{p.period}</td>
                  <td className="py-1.5 pr-3 font-mono tabular-nums">
                    {money(p.gross_minor, p.currency)}
                  </td>
                  <td className="py-1.5 font-mono tabular-nums">
                    {money(p.net_minor, p.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </TabState>
  );
}

// ── Duty History (with start/end odometer photos) ───────────────────────────
const DUTY_COLOR: Record<DutySession['status'], string> = {
  ACTIVE: 'text-emerald-300',
  ENDED: 'text-slate-300',
  CANCELLED: 'text-rose-300',
};

function DutyHistoryTab({ user }: { user: FieldUserRow }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['fu-duty-sessions', user.id],
    queryFn: () => operationsApi.listDutySessionsForUser(user.id),
  });

  return (
    <TabState
      isLoading={isLoading}
      error={error}
      isEmpty={(data ?? []).length === 0}
      emptyLabel="No duty sessions recorded for this field user."
    >
      <ul className="space-y-3">
        {((data as DutySession[] | undefined) ?? []).map((d) => (
          <li key={d.id} className="rounded-md border border-border p-3">
            <div className="flex items-center justify-between text-sm">
              <span>{fmtDateTime(d.started_at)}</span>
              <span className={DUTY_COLOR[d.status]}>{d.status}</span>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {d.start_odometer.toLocaleString()} →{' '}
              {d.end_odometer != null ? d.end_odometer.toLocaleString() : '—'} ·{' '}
              {(d.distance_m / 1000).toFixed(1)} km · {d.work_unit_count} WU
              {d.ended_at ? ` · ended ${fmtDateTime(d.ended_at)}` : ''}
            </p>
            <div className="mt-2 flex gap-2">
              <MediaThumb path={d.start_odometer_photo_path} label="start" />
              <MediaThumb path={d.end_odometer_photo_path} label="end" />
            </div>
          </li>
        ))}
      </ul>
    </TabState>
  );
}

// ── Work Unit History ───────────────────────────────────────────────────────
function WorkUnitsTab({ user }: { user: FieldUserRow }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['fu-work-units', user.id],
    queryFn: () => operationsApi.listWorkUnitsForUser(user.id),
  });
  const engagements = useQuery({
    queryKey: ['engagements'],
    queryFn: () => operationsApi.listEngagements(),
  });

  const engName = useMemo(() => {
    const m: Record<string, string> = {};
    (engagements.data ?? []).forEach((e: EngagementDetail) => (m[e.id] = e.name));
    return m;
  }, [engagements.data]);

  return (
    <TabState
      isLoading={isLoading}
      error={error}
      isEmpty={(data ?? []).length === 0}
      emptyLabel="No work units recorded for this field user."
    >
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr className="border-b border-border">
            <th className="py-2 pr-3">Started</th>
            <th className="py-2 pr-3">Engagement</th>
            <th className="py-2 pr-3">Ref</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2">Proof</th>
          </tr>
        </thead>
        <tbody>
          {((data as WorkUnit[] | undefined) ?? []).map((w) => {
            const proof = (w.proof_paths?.items ?? []) as string[];
            return (
              <tr key={w.id} className="border-b border-border/60 align-top">
                <td className="py-1.5 pr-3 text-xs">{fmtDateTime(w.started_at)}</td>
                <td className="py-1.5 pr-3 text-muted-foreground">
                  {w.engagement_id ? (engName[w.engagement_id] ?? '—') : '—'}
                </td>
                <td className="py-1.5 pr-3 font-mono text-xs">{w.reference_number ?? '—'}</td>
                <td className="py-1.5 pr-3">{w.status}</td>
                <td className="py-1.5">
                  {proof.length === 0 ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {proof.map((p, i) => (
                        <MediaThumb key={i} path={p} label={`#${i + 1}`} />
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </TabState>
  );
}

// ── Attendance (last 30 days) ───────────────────────────────────────────────
const ATT_COLOR: Record<AttendanceRow['status'], string> = {
  PRESENT: 'text-emerald-300',
  PARTIAL: 'text-amber-300',
  ABSENT: 'text-rose-300',
  LEAVE: 'text-cyan-300',
  HOLIDAY: 'text-slate-300',
  WEEKEND: 'text-slate-400',
};

function AttendanceTab({ user }: { user: FieldUserRow }) {
  const range = useMemo(() => lastDays(30), []);
  const { data, isLoading, error } = useQuery({
    queryKey: ['fu-attendance', user.id, range.start, range.end],
    queryFn: () => operationsApi.listAttendance(user.id, range),
  });

  return (
    <div>
      <p className="mb-2 text-xs text-muted-foreground">Last 30 days</p>
      <TabState
        isLoading={isLoading}
        error={error}
        isEmpty={(data ?? []).length === 0}
        emptyLabel="No attendance rows — derived nightly from duty sessions, or entered via correction."
      >
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-foreground">
            <tr className="border-b border-border">
              <th className="py-2 pr-3">Date</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Worked</th>
              <th className="py-2">OT</th>
            </tr>
          </thead>
          <tbody>
            {((data as AttendanceRow[] | undefined) ?? []).map((a) => (
              <tr key={a.id} className="border-b border-border/60">
                <td className="py-1.5 pr-3">{fmtDate(a.work_date)}</td>
                <td className={`py-1.5 pr-3 ${ATT_COLOR[a.status]}`}>{a.status}</td>
                <td className="py-1.5 pr-3 tabular-nums">{a.worked_hours.toFixed(1)}h</td>
                <td className="py-1.5 tabular-nums">{a.overtime_hours.toFixed(1)}h</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TabState>
    </div>
  );
}

// ── Salary Structure ────────────────────────────────────────────────────────
function SalaryTab({ user }: { user: FieldUserRow }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['fu-salary', user.id],
    queryFn: () => financialApi.listStructures(user.id),
  });

  return (
    <TabState
      isLoading={isLoading}
      error={error}
      isEmpty={(data ?? []).length === 0}
      emptyLabel="No salary structure configured for this field user."
    >
      <div className="space-y-4">
        {((data as SalaryStructure[] | undefined) ?? []).map((s) => (
          <div key={s.id} className="rounded-md border border-border p-3">
            <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {fmtDate(s.effective_from)} → {s.effective_to ? fmtDate(s.effective_to) : 'current'}
              </span>
              {s.effective_to === null && (
                <span className="rounded bg-emerald-500/15 px-2 py-0.5 text-emerald-300">
                  active
                </span>
              )}
            </div>
            <p className="text-sm">
              Base <span className="font-mono tabular-nums">{money(s.base_minor, s.currency)}</span>
            </p>
            {s.allowances.length > 0 && (
              <ul className="mt-2 space-y-1 text-sm">
                {s.allowances.map((al, i) => (
                  <li key={i} className="flex justify-between text-muted-foreground">
                    <span>{al.label}</span>
                    <span className="font-mono tabular-nums">
                      {money(al.amount_minor, s.currency)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {s.notes && <p className="mt-2 text-xs text-muted-foreground">{s.notes}</p>}
          </div>
        ))}
      </div>
    </TabState>
  );
}

// ── Recent Alerts (tenant feed, filtered to this user) ──────────────────────
const ALERT_COLOR: Record<AlertDto['severity'], string> = {
  INFO: 'bg-sky-500/15 text-sky-300',
  WARNING: 'bg-amber-500/15 text-amber-300',
  CRITICAL: 'bg-rose-500/15 text-rose-300',
};

function AlertsTab({ user }: { user: FieldUserRow }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['alerts', 'recent'],
    queryFn: () => commandCenterApi.listAlerts({ limit: 200 }),
  });

  const rows = useMemo(
    () => (data ?? []).filter((a) => a.field_user_id === user.id),
    [data, user.id],
  );

  return (
    <TabState
      isLoading={isLoading}
      error={error}
      isEmpty={rows.length === 0}
      emptyLabel="No alerts raised for this field user."
    >
      <ul className="space-y-2">
        {rows.map((a) => (
          <li key={a.id} className="rounded-md border border-border p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{a.title}</span>
              <span className={`rounded px-2 py-0.5 text-xs ${ALERT_COLOR[a.severity]}`}>
                {a.severity}
              </span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{a.message}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {a.status} · {fmtDateTime(a.raised_at)}
            </p>
          </li>
        ))}
      </ul>
    </TabState>
  );
}
